import { Shield, TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import type { Diagnostic, NetworkKinds } from '../../api/types';
import { strings } from '../../strings';
import { NetworkDiagnostic } from '../lists/parts';
import { providerLabel } from '../shared';
import { AllowedEgressList } from './AllowedEgressList';
import { GcpFirewallCard } from './GcpFirewall';
import { AWS_CENTRAL_EGRESS, AWS_CENTRAL_EGRESS_MAX_HUB_PREFIX, money, type FirewallForm } from './helpers';

const t = strings.networks.topology;

const tierLabel = (tier: string) =>
  tier === 'network-firewall' ? 'Network Firewall' : tier.charAt(0).toUpperCase() + tier.slice(1);

/**
 * The Settings tab's Firewall section (spec 11 6.15.12): per provider a switch (off by default), the tier with its price
 * per region and month, the egress choice and the allowed host names; diagnostics at their rows.
 */
export function FirewallSection({
  entries,
  value,
  readOnly,
  turnedOnByChoice,
  microSegmentation,
  hubPrefixLength = 22,
  regionCount = {},
  diagnosticsAt,
  onChange,
}: {
  entries: NetworkKinds['firewalls'];
  /** Provider → the form's firewall (null: off and never set). */
  value: Record<string, FirewallForm | null>;
  readOnly: boolean;
  /** Providers whose firewall the "Through the hub firewall" choice turned on. */
  turnedOnByChoice: readonly string[];
  /** The form's topology is micro-segmentation (egress isn't filtered without the firewall's egress). */
  microSegmentation: boolean;
  /** The address plan's hub prefix length (AWS central egress needs /25 or larger hubs). */
  hubPrefixLength?: number;
  /** Provider → the number of regions the form enables (the AWS saving note). */
  regionCount?: Record<string, number>;
  diagnosticsAt: (path: string) => Diagnostic[];
  onChange: (provider: string, firewall: FirewallForm | null) => void;
}) {
  const ids = useId();
  if (!entries.length) return <p className="help">{t.noFirewallKind}</p>;
  return (
    <div className="space-y-4">
      {entries.map((entry) => {
        const p = entry.provider;
        const fw = value[p] ?? null;
        // Google Cloud (feature spec 17, phase 6): Cloud NGFW Standard filters only egress, so its card differs.
        if (p === 'gcp')
          return (
            <GcpFirewallCard
              key={p}
              entry={entry}
              value={fw}
              readOnly={readOnly}
              turnedOnByChoice={turnedOnByChoice.includes(p)}
              diagnosticsAt={diagnosticsAt}
              onChange={(next) => onChange(p, next)}
            />
          );
        const on = !!fw?.enabled;
        const base = `/settings/providers/${p}/firewall`;
        const switchDiagnostics = [
          ...diagnosticsAt(`${base}/enabled`),
          ...diagnosticsAt('/settings/topology/spokeToSpoke'),
        ].filter((d) => d.code !== 'TOPOLOGY_CHOICE_REQUIRED');
        const sectionDiagnostics = diagnosticsAt(base).filter(
          (d) => d.path === base && d.code !== 'EGRESS_NOT_FILTERED',
        );
        const aws = p === 'aws';
        const central = on && fw?.egress === 'through-firewall';
        // AWS (phase 4): egress isn't filtered in any topology until the firewall filters it (central egress).
        const egressNotFiltered = aws ? !central : microSegmentation && !central;
        const cost = (tier: string) => entry.tierCosts?.[tier];
        const tier = fw?.tier ?? entry.tiers.find((x) => x === 'standard') ?? entry.tiers[0];
        const asOf = Object.values(entry.tierCosts ?? {})[0]?.asOf;
        const hubTooSmall = aws && hubPrefixLength > AWS_CENTRAL_EGRESS_MAX_HUB_PREFIX;
        const awsHelp = () => {
          const c = cost(tier);
          const lines = c ? [t.awsFirewallCost(money(c), c.asOf)] : [tierLabel(tier)];
          if (central) lines.push(t.awsCentralEgressCost(money(AWS_CENTRAL_EGRESS)));
          if (turnedOnByChoice.includes(p) && c) {
            const regions = Math.max(1, regionCount[p] ?? 1);
            const sum = (c.monthly + (central ? AWS_CENTRAL_EGRESS.monthly : 0)) * regions;
            lines.push(t.awsTurnedOnForHubSpoke(money({ monthly: sum, currency: c.currency })));
          }
          return lines;
        };
        const hosts = fw?.allowedEgress ?? [];
        const update = (next: Partial<FirewallForm>) =>
          onChange(p, {
            enabled: on,
            tier,
            egress: fw?.egress ?? 'direct',
            allowedEgress: hosts,
            ...next,
          });
        return (
          <fieldset key={p} className="net-provider-block" data-testid={`firewall-${p}`}>
            <legend className="sr-only">{aws ? t.awsFirewall : t.firewallSwitch(providerLabel(p))}</legend>
            <label className="net-switch-label flex items-center gap-2 font-semibold">
              <input
                type="checkbox"
                role="switch"
                checked={on}
                disabled={readOnly}
                aria-describedby={`${ids}-${p}-switch-help`}
                onChange={(e) =>
                  e.target.checked
                    ? update({ enabled: true })
                    : onChange(p, fw ? { ...fw, enabled: false } : null)
                }
              />
              <Shield aria-hidden className="size-4 text-fg-muted" />
              {aws ? t.awsFirewall : t.firewallSwitch(providerLabel(p))}
            </label>
            <p id={`${ids}-${p}-switch-help`} className="help mt-1">
              {aws
                ? awsHelp().map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))
                : on
                  ? turnedOnByChoice.includes(p)
                    ? t.firewallTurnedOnBy
                    : cost(tier)
                      ? t.tierOption(
                          tierLabel(tier),
                          `${cost(tier)!.currency} ${cost(tier)!.monthly.toLocaleString('en-US')}`,
                        )
                      : tierLabel(tier)
                  : t.firewallOffHelp}
            </p>
            {aws && !on && <p className="help">{t.firewallOffHelp}</p>}
            {switchDiagnostics.map((d, i) => (
              <p key={i} className="mt-1">
                <NetworkDiagnostic diagnostic={d} />
              </p>
            ))}
            {on && (
              <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${ids}-${p}-tier`} className="label">
                    {t.tier}
                  </label>
                  <select
                    id={`${ids}-${p}-tier`}
                    className="input"
                    value={tier}
                    disabled={readOnly}
                    aria-describedby={`${ids}-${p}-tier-help`}
                    onChange={(e) => update({ tier: e.target.value })}
                  >
                    {entry.tiers.map((x) => {
                      const c = cost(x);
                      return (
                        <option key={x} value={x}>
                          {c
                            ? t.tierOption(tierLabel(x), `${c.currency} ${c.monthly.toLocaleString('en-US')}`)
                            : tierLabel(x)}
                        </option>
                      );
                    })}
                  </select>
                  {asOf && (
                    <p id={`${ids}-${p}-tier-help`} className="help">
                      {t.listPrice(asOf)}
                    </p>
                  )}
                </div>
                <fieldset>
                  <legend className="label">{t.egress}</legend>
                  {(
                    [
                      ['direct', t.egressDirect, t.egressDirectHelp],
                      [
                        'through-firewall',
                        aws ? t.awsEgressFirewall : t.egressFirewall,
                        aws ? t.awsEgressFirewallHelp : t.egressFirewallHelp,
                      ],
                    ] as const
                  ).map(([v, label, help]) => {
                    const blocked = v === 'through-firewall' && hubTooSmall;
                    return (
                      <label key={v} className="mb-1 flex items-start gap-2 text-sm">
                        <input
                          type="radio"
                          name={`${ids}-${p}-egress`}
                          className="mt-1"
                          checked={(fw?.egress ?? 'direct') === v}
                          disabled={readOnly || blocked}
                          aria-describedby={blocked ? `${ids}-${p}-hub-too-small` : undefined}
                          onChange={() => update({ egress: v })}
                        />
                        <span>
                          <span className="font-medium">{label}</span>
                          <span className="block text-[12.5px] text-fg-muted">{help}</span>
                          {v === 'through-firewall' && aws && (
                            <span className="block text-[12.5px] text-fg-muted">
                              {t.awsCentralEgressCost(money(AWS_CENTRAL_EGRESS))}
                            </span>
                          )}
                          {blocked && (
                            <span
                              id={`${ids}-${p}-hub-too-small`}
                              className="block text-[12.5px] text-warning"
                            >
                              {t.awsHubTooSmall}{' '}
                              <code className="font-mono text-xs">AWS_CENTRAL_EGRESS_HUB_TOO_SMALL</code>
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                  {diagnosticsAt(`${base}/egress`).map((d, i) => (
                    <p key={i} className="mt-1">
                      <NetworkDiagnostic diagnostic={d} />
                    </p>
                  ))}
                </fieldset>
                <AllowedEgressList
                  hosts={hosts}
                  readOnly={readOnly}
                  help={t.allowedEgressHelp}
                  diagnosticsAt={(i) => diagnosticsAt(`${base}/allowedEgress/${i}`)}
                  onChange={(allowedEgress) => update({ allowedEgress })}
                />
              </div>
            )}
            {sectionDiagnostics.map((d, i) => (
              <p key={i} className="mt-2">
                <NetworkDiagnostic diagnostic={d} />
              </p>
            ))}
            {egressNotFiltered && (
              <p className="callout callout-warning mt-3" data-testid="egress-not-filtered">
                <TriangleAlert aria-hidden className="size-4 shrink-0" />
                <span>
                  {aws ? t.awsEgressNotFiltered : strings.networks.requests.egressNotFiltered}{' '}
                  <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
                </span>
              </p>
            )}
          </fieldset>
        );
      })}
    </div>
  );
}
